/**
 * Editorial header links are authored in Sharetribe Console (Content → Top bar
 * → custom links). The label (`text`) and the destination (`href`) live on the
 * hosted top-bar asset. This file only marks the link for the page being viewed.
 */
import React from 'react';
import classNames from 'classnames';

import { ExternalLink, NamedLink } from '../../../components';

/**
 * Topbar builds `currentPage` for CMS pages as `CMSPage:<pageId>` (see
 * Topbar.js), so a plain route-name comparison would mark every CMS page as
 * current. Match the handle it actually produces.
 *
 * @param {Object} linkConfig resolved Console link ({ text, type, href, route })
 * @param {string} currentPage resolved current page handle
 * @returns {boolean}
 */
export const isCurrentCustomLink = (linkConfig, currentPage) => {
  const { route } = linkConfig || {};
  if (!route?.name || !currentPage) {
    return false;
  }
  const { name, params } = route;
  if (name === 'CMSPage') {
    return currentPage === `CMSPage:${params?.pageId}`;
  }
  if (name === 'InboxPage') {
    return currentPage === `InboxPage:${params?.tab}`;
  }
  return currentPage === name;
};

/**
 * One Console custom link, as an in-app route when the href matches a route,
 * otherwise as an external URL. Label text is the Console value, not a
 * translation key.
 *
 * @param {Object} props
 * @param {Object} props.linkConfig
 * @param {string} props.currentPage
 * @param {string} [props.itemClassName]
 * @param {string} [props.linkClassName]
 * @param {string} [props.currentItemClassName] applied to the `<li>` when current
 * @param {string} [props.currentLinkClassName] applied to the anchor when current
 */
export const ConsoleNavItem = ({
  linkConfig,
  currentPage,
  itemClassName,
  linkClassName,
  currentItemClassName,
  currentLinkClassName,
}) => {
  const { text, type, href, route } = linkConfig;
  const current = isCurrentCustomLink(linkConfig, currentPage);
  const itemClass = classNames(itemClassName, current && currentItemClassName);
  const linkClass = classNames(linkClassName, current && currentLinkClassName);

  if (type === 'internal' && route) {
    const { name, params, to } = route;
    return (
      <li className={itemClass}>
        <NamedLink className={linkClass} name={name} params={params} to={to}>
          {text}
        </NamedLink>
      </li>
    );
  }

  return (
    <li className={itemClass}>
      <ExternalLink className={linkClass} href={href}>
        {text}
      </ExternalLink>
    </li>
  );
};
